import { useState, useEffect, useMemo, useRef } from "react"
import { array, object, bool, func, number } from "prop-types"
import { HelperShelfMessage, HelperShelfTrigger } from "components/helper_shelf"
import axios from "axios"
import Icon from "components/planning_center_icon"
import pcoUrl from "@planningcenter/url"
import AccountCenterSearch from "components/admin/account_center_search/account_center_search"
import { Box, Spinner } from "@planningcenter/tapestry-react"
import useApiAction from "hooks/use_api_action"

const SignupSubscribers = ({ subscribers: subscribersFromProps, event, isActingStaff }) => {
  const [subscribers, setSubscribers] = useState(subscribersFromProps)
  const [creatingNewSubscription, setCreatingNewSubscription] = useState(false)
  const [deletingSubscriber, setDeletingSubscriber] = useState(false)
  const disabledItemCondition = item => {
    return subscribers.map(subscriber => subscriber.id).includes(item.account_center_id)
  }
  const [staffButtonDisabled, setStaffButtonDisabled] = useState(false)

  const resetFormFunc = useRef(null)

  useEffect(() => {
    if (!creatingNewSubscription && typeof resetFormFunc.current === "function") {
      resetFormFunc.current()
    }
  }, [creatingNewSubscription])

  const handleCreateSubscriberClick = (person, state) => {
    if (person && person.account_center_id) {
      setCreatingNewSubscription(true)
      if (
        typeof resetFormFunc.current !== "function" &&
        state &&
        typeof state.clearSelection === "function"
      ) {
        resetFormFunc.current = state.clearSelection
      }
      axios
        .post(
          `${pcoUrl(railsEnv)("api")}/registrations/v2/events/${event.id}/add_subscriber`,
          {
            person_account_center_id: person.account_center_id
          },
          {
            withCredentials: true,
            headers: {
              "X-CSRF-Token": $.rails.csrfToken()
            }
          }
        )
        .then(resp => {
          setSubscribers(resp.data.data)
          setCreatingNewSubscription(false)
        })
    }
  }

  const handleDeleteSubscriberClick = subscriber => {
    if (deletingSubscriber !== subscriber.id) {
      setDeletingSubscriber(subscriber.id)
      axios
        .post(
          `${pcoUrl(railsEnv)("api")}/registrations/v2/events/${event.id}/remove_subscriber`,
          {
            person_account_center_id: subscriber.id
          },
          {
            withCredentials: true,
            headers: {
              "X-CSRF-Token": $.rails.csrfToken()
            }
          }
        )
        .then(resp => {
          setDeletingSubscriber(null)
          setSubscribers(resp.data.data)
        })
    }
  }

  const forceDigestAction = useApiAction({
    path: `trigger_digest`
  })

  const sortedSubscribers = useMemo(() => {
    return subscribers.sort((a, b) => {
      return a.attributes.name.toUpperCase() > b.attributes.name.toUpperCase() ? 1 : -1
    })
  }, [subscribers])

  return (
    <div className="pane">
      <div className="pane__header">
        <h2 className="pane__title">
          Subscribers <HelperShelfTrigger id="subscribers_helper" className="p-r t-1p pl-1" />
        </h2>
        {isActingStaff && (
          <div>
            <button
              onClick={() => {
                setStaffButtonDisabled(true)
                forceDigestAction.run().then(() => setStaffButtonDisabled(false))
              }}
              className="staff-only btn-outline sm"
              disabled={staffButtonDisabled}
            >
              Send a digest of today's activity for this Org
            </button>
          </div>
        )}
      </div>
      <HelperShelfMessage id="subscribers_helper" className="p-2">
        Subscribers receive a daily digest of new registration activity, as well as partial payment
        notifications. Adding someone as a subscriber will not give them any administrative
        privileges.{" "}
        <a
          target="_blank"
          rel="noreferrer"
          href="https://pcoregistrations.zendesk.com/hc/en-us/articles/204462680-Manage-Signup-Settings#communication-6"
        >
          Learn More
        </a>
      </HelperShelfMessage>
      <div className="p-2">
        <div className="p-r">
          <AccountCenterSearch
            onSelect={handleCreateSubscriberClick}
            disabledItemCondition={item => disabledItemCondition(item)}
            DisableditemRenderer={DisabledSearchResult}
            inputProps={{
              disabled: creatingNewSubscription,
              placeholder: "Search for someone to subscribe",
              autoFocus: false
            }}
            showSpinner={creatingNewSubscription}
            menuProps={{
              style: { zIndex: 1 }
            }}
          />
          <div className="managers-list">
            <ul className="action-list mb-0 unstyled">
              {sortedSubscribers.map(subscriber => (
                <Subscriber
                  key={subscriber.id}
                  subscriber={subscriber}
                  onClick={handleDeleteSubscriberClick}
                  showSpinner={deletingSubscriber === subscriber.id}
                />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

const Subscriber = ({ subscriber, onClick, showSpinner = false }) => {
  const {
    attributes: { name }
  } = subscriber
  return (
    <li className="d-f jc-sb action-list__item">
      <div className="p-1">{name}</div>
      {showSpinner ? (
        <Box paddingHorizontal={2} paddingTop={1}>
          <Spinner size="xs" />
        </Box>
      ) : (
        <button
          className={`action-list-item__remove ${showSpinner && "active"}`}
          onClick={() => onClick(subscriber)}
        >
          <span className="p-r t-2p">
            <Icon
              symbol="general#x"
              aria-label="Remove this person's subscription from this signup"
            />
          </span>
        </button>
      )}
    </li>
  )
}

const DisabledSearchResult = ({ item, getItemProps, index }) => {
  return (
    <li
      {...getItemProps({
        key: item.account_center_id,
        index,
        item,
        disabled: true,
        className: "autocomplete-result inactive"
      })}
    >
      <button className="autocomplete-result__link">
        <div className="mr-1">
          <img src={item.cropped_avatar_url} alt={item.name} className="w-5 h-5 circle" />
        </div>
        <div className="autocomplete-result__details-wrapper fw-w">
          <div className="d-f ai-c">
            <div className="autocomplete-result__name">{item.name}</div>
          </div>
          <div className="w-100% mt-4p">This person is already subscribed</div>
        </div>
      </button>
    </li>
  )
}

DisabledSearchResult.propTypes = {
  item: object,
  getItemProps: func,
  index: number
}

Subscriber.propTypes = {
  subscriber: object,
  onClick: func,
  showSpinner: bool
}

SignupSubscribers.propTypes = {
  subscribers: array,
  event: object,
  isActingStaff: bool
}

export default SignupSubscribers
